import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// PXO: Load translations for inventory status display (SSOT)
let pxoTranslations = {};
try {
  const translationEl = document.getElementById('PxoTranslations');
  if (translationEl) {
    pxoTranslations = JSON.parse(translationEl.textContent);
  } else {
    console.warn('[PXO] Translations script #PxoTranslations not found.');
  }
} catch (e) {
  console.error('[PXO] Failed to parse translations:', e);
}

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    // (PXO) Initialize button state with SSOT logic
    // Use requestAnimationFrame to ensure ProductFormComponent is initialized first
    requestAnimationFrame(() => {
      this.#initializeButtonState();
    });

    // Listen for variant changes to update button
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);

    // (PXO) Remove variant change listener
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
  }

  /**
   * (PXO) Initialize button state based on SSOT availability logic
   */
  #initializeButtonState() {
    const button = this.querySelector('[data-pxo-atc-button]');
    const buttonText = this.querySelector('[data-pxo-button-text]');

    if (!button || !buttonText) {
      return;
    }

    // Get product data from product-form-component (correct tag name)
    const productForm = this.closest('product-form-component');
    if (!productForm) {
      return;
    }

    // Get product JSON to determine status
    const productId = productForm.dataset.productId;
    const scriptId = `ProductJson-${productId}`;
    const productJsonEl = document.getElementById(scriptId);
    
    if (!productJsonEl) {
      // ProductJson not found - normal for product grid context
      return;
    }

    let productJson;
    try {
      productJson = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.error('[PXO ATC Button] Failed to parse product JSON:', e);
      return;
    }

    const status = getAvailabilityStatus(productJson);
    const translations = window.pxoTranslations || {};

    console.log('[PXO ATC Button] Status:', status);
    console.log('[PXO ATC Button] Translations:', translations);

    // Update button text and state based on SSOT status
    switch (status) {
      case 'PRE-ORDER':
        buttonText.textContent = translations.actions_preorder || 'Pre-order';
        button.disabled = false;
        console.log('[PXO ATC Button] → Set to PRE-ORDER');
        break;

      case 'IN-STOCK':
        buttonText.textContent = translations.actions_add_to_cart || 'Add to cart';
        button.disabled = false;
        console.log('[PXO ATC Button] → Set to IN-STOCK');
        break;

      case 'BACK-ORDER':
        buttonText.textContent = translations.actions_backorder || 'Backorder';
        button.disabled = false;
        console.log('[PXO ATC Button] → Set to BACK-ORDER');
        break;

      case 'OUT-OF-STOCK':
        buttonText.textContent = translations.actions_sold_out || 'Sold out';
        button.disabled = true;
        console.log('[PXO ATC Button] → Set to OUT-OF-STOCK (disabled)');
        break;

      default:
        buttonText.textContent = translations.actions_sold_out || 'Sold out';
        button.disabled = true;
        console.log('[PXO ATC Button] → Set to default (disabled)');
    }
  }

  /**
   * (PXO) Handle variant change events
   */
  #handleVariantChange = () => {
    this.#initializeButtonState();
  };

  /**
   * (PXO) Public method to update button state (called after morph)
   */
  updateButtonState() {
    this.#initializeButtonState();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // (PXO) Check if ProductJson exists before initializing PXO features
    // ProductJson only exists on PDP, not in product grid/cards
    const productJson = this.#getProductJson();
    if (!productJson) {
      // ProductJson not found - normal for product grid context, skip PXO initialization
      return;
    }

    // PXO: Initialize inventory status on first load (PDP only)
    this.#initializeInventoryStatus();

    // (PXO) Initialize default variant based on status (PRE-ORDER/BACK-ORDER → select random variant)
    this.#initializeDefaultVariant();

    // (PXO) Initialize accelerated checkout visibility on first load
    this.#initializeAcceleratedCheckout();
  }

  /**
   * (PXO) Retrieves full product JSON from DOM script tag
   * @returns {Object | null} Product JSON or null
   */
  #getProductJson() {
    const scriptId = `ProductJson-${this.dataset.productId}`;
    const productJsonEl = document.getElementById(scriptId);
    if (!productJsonEl) {
      // ProductJson not found - normal for product grid context
      return null;
    }
    try {
      return JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.error(`[PXO] Failed to parse product JSON:`, e);
      return null;
    }
  }

  /**
   * (PXO) Initialize inventory status on page load
   */
  #initializeInventoryStatus() {
    try {
      // (PXO DEBUG) Find container globally using product ID
      const inventoryContainer = document.querySelector(
        `[data-product-id="${this.dataset.productId}"] [data-pxo-inventory-status]`
      );
      console.log('[PXO DEBUG] Init - Product ID:', this.dataset.productId);
      console.log('[PXO DEBUG] Init - Container:', inventoryContainer);
      
      const productJson = this.#getProductJson();
      if (!inventoryContainer || !productJson) {
        console.warn('[PXO DEBUG] Init failed - Container:', !!inventoryContainer, 'Product:', !!productJson);
        return;
      }

      // Get initial variant from form
      const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
      if (!variantIdInput?.value) return;

      const selectedVariant = productJson.variants?.find(
        /** @param {any} v */
        (v) => v.id.toString() === variantIdInput.value
      );

      if (selectedVariant && pxoTranslations) {
        updateProductInventory(inventoryContainer, productJson, selectedVariant, pxoTranslations);
      }
    } catch (e) {
      console.error('[PXO] Error initializing inventory status:', e);
    }
  }

  /**
   * (PXO) Initialize accelerated checkout visibility on page load
   */
  #initializeAcceleratedCheckout() {
    if (!this.refs.acceleratedCheckoutButtonContainer) return;

    const productJson = this.#getProductJson();
    if (!productJson) return;

    const masterStatus = getAvailabilityStatus(productJson);
    const hideAcceleratedCheckout = masterStatus === 'OUT-OF-STOCK' || 
                                     masterStatus === 'PRE-ORDER' || 
                                     masterStatus === 'BACK-ORDER';
    
    if (hideAcceleratedCheckout) {
      this.refs.acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      console.log('[PXO] Init - Hiding accelerated checkout - Status:', masterStatus);
    } else {
      this.refs.acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      console.log('[PXO] Init - Showing accelerated checkout - Status:', masterStatus);
    }

    // (PXO) Show/hide info boxes based on status
    this.#updateInfoBoxes(masterStatus);
  }

  /**
   * (PXO) Show/hide PRE-ORDER and BACK-ORDER info boxes based on status
   * @param {'PRE-ORDER' | 'IN-STOCK' | 'BACK-ORDER' | 'OUT-OF-STOCK'} status
   */
  #updateInfoBoxes(status) {
    console.log('[PXO INFO] updateInfoBoxes called - Status:', status);
    const preorderBox = document.querySelector('[data-pxo-preorder-info]');
    const backorderBox = document.querySelector('[data-pxo-backorder-info]');

    console.log('[PXO INFO] Found boxes - PRE-ORDER:', preorderBox, 'BACK-ORDER:', backorderBox);

    if (!preorderBox || !backorderBox) {
      console.warn('[PXO INFO] Missing info boxes! PRE-ORDER:', !!preorderBox, 'BACK-ORDER:', !!backorderBox);
      return;
    }

    // Hide both by default (use display: none for reliability)
    /** @type {HTMLElement} */ (preorderBox).style.display = 'none';
    /** @type {HTMLElement} */ (backorderBox).style.display = 'none';
    preorderBox.setAttribute('hidden', 'true');
    backorderBox.setAttribute('hidden', 'true');

    // Show appropriate box
    if (status === 'PRE-ORDER') {
      /** @type {HTMLElement} */ (preorderBox).style.display = 'flex';
      preorderBox.removeAttribute('hidden');
      console.log('[PXO INFO] Showing PRE-ORDER info box');
    } else if (status === 'BACK-ORDER') {
      /** @type {HTMLElement} */ (backorderBox).style.display = 'flex';
      backorderBox.removeAttribute('hidden');
      console.log('[PXO INFO] Showing BACK-ORDER info box');
    } else {
      console.log('[PXO INFO] Hiding both info boxes - Status:', status);
    }
  }

  /**
   * (PXO) Find random variant based on title (contains 'random' or 'rnd')
   * @returns {Object | null} Random variant or null
   */
  #findRandomVariant() {
    console.log('[PXO ATC] Finding random variant...');
    const productJson = this.#getProductJson();
    if (!productJson?.variants) {
      console.warn('[PXO ATC] No product variants found');
      return null;
    }

    const randomVariant = productJson.variants.find(
      /** @param {any} variant */
      (variant) => {
        const title = (variant.title || '').toLowerCase();
        const hasRandom = title.includes('random') || title.includes('rnd');
        console.log('[PXO ATC] Checking variant:', variant.title, '→ hasRandom:', hasRandom);
        return hasRandom;
      }
    );

    if (randomVariant) {
      console.log('[PXO ATC] ✓ Found random variant:', randomVariant.title, 'ID:', randomVariant.id);
    } else {
      console.log('[PXO ATC] ✗ No random variant found');
    }
    return randomVariant || null;
  }

  /**
   * (PXO) Initialize default variant based on availability status
   * Implements variant option visibility rules based on status
   */
  #initializeDefaultVariant() {
    console.log('[PXO ATC] initializeDefaultVariant called');
    
    const productJson = this.#getProductJson();
    if (!productJson) {
      console.warn('[PXO ATC] No product JSON, skipping default variant init');
      return;
    }

    const status = getAvailabilityStatus(productJson);
    console.log('[PXO ATC] Product status:', status);

    switch (status) {
      case 'PRE-ORDER':
      case 'BACK-ORDER': {
        // PRE-ORDER/BACK-ORDER: Random 있으면 base 숨김 + random 활성화, 없으면 base 활성화 + set 숨김
        console.log('[PXO ATC] PRE-ORDER/BACK-ORDER: Checking for random variant...');
        const randomVariant = this.#findRandomVariant();

        if (randomVariant) {
          console.log('[PXO ATC] ✓ Random found → Hide base, show random, hide set');
          // Select random variant (silently - don't trigger variant change event in quick-add modal)
          const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
          const isQuickAddModal = this.closest('dialog#quick-add-dialog') !== null;
          
          if (variantIdInput && variantIdInput.value !== randomVariant.id.toString()) {
            variantIdInput.value = randomVariant.id.toString();
            
            // Only dispatch change event if NOT in quick-add modal
            if (!isQuickAddModal) {
              variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
            console.log('[PXO ATC] ✓ Random variant selected:', randomVariant.id, '(quick-add:', isQuickAddModal, ')');
          } else {
            console.log('[PXO ATC] ✓ Random variant already selected:', randomVariant.id, '(quick-add:', isQuickAddModal, ')');
          }
          
          // Hide base, show random, hide set (this will also check the random radio button)
          this.#applyVariantVisibilityRules('hide-base-and-set');
        } else {
          console.log('[PXO ATC] ✗ No random → Show base, hide random, hide set');
          // Show base, hide random/set
          this.#applyVariantVisibilityRules('hide-random-and-set');
        }
        break;
      }

      case 'IN-STOCK':
        // IN-STOCK: random 숨김/비활성화, set 숨김, base 활성화
        console.log('[PXO ATC] IN-STOCK → Hide random, hide set, show base');
        this.#applyVariantVisibilityRules('hide-random-and-set');
        break;

      case 'OUT-OF-STOCK':
        // OUT-OF-STOCK: random 숨김, set 숨김, base 비활성화(선긋기)
        console.log('[PXO ATC] OUT-OF-STOCK → Hide random, hide set, disable base');
        this.#applyVariantVisibilityRules('hide-random-and-set-disable-base');
        break;

      default:
        console.log('[PXO ATC] Unknown status, showing all options');
        this.#applyVariantVisibilityRules('show-all');
    }
  }

  /**
   * (PXO) Apply variant visibility rules based on status
   * @param {'hide-base-and-set' | 'hide-random-and-set' | 'hide-random-and-set-disable-base' | 'show-all'} rule
   */
  #applyVariantVisibilityRules(rule) {
    console.log('[PXO ATC] Applying rule:', rule);
    
    // (PXO) variant-picker is a sibling, not a child - search in parent or globally
    const variantPicker = this.parentElement?.querySelector('[data-pxo-variant-picker]') || 
                          document.querySelector('[data-pxo-variant-picker]');
    if (!variantPicker) {
      console.warn('[PXO ATC] No variant picker found');
      return;
    }

    const labels = variantPicker.querySelectorAll('.variant-option__button-label');
    console.log('[PXO ATC] Found', labels.length, 'variant labels');

    // (PXO) Count visible variants after applying rules
    let visibleCount = 0;

    labels.forEach((label) => {
      const input = /** @type {HTMLInputElement | null} */ (label.querySelector('input[type="radio"]'));
      if (!input) return;

      const value = input.value.toLowerCase();
      const isRandom = value.includes('random') || value.includes('rnd');
      const isSet = value.includes('set');
      const isBase = !isRandom && !isSet;

      console.log('[PXO ATC] Variant:', input.value, '→ random:', isRandom, 'set:', isSet, 'base:', isBase);

      switch (rule) {
        case 'hide-base-and-set':
          // PRE-ORDER/BACK-ORDER with random: base 숨김, random 활성화, set 숨김
          if (isBase || isSet) {
            /** @type {HTMLElement} */ (label).style.display = 'none';
            input.disabled = true;
            console.log('[PXO ATC] → Hiding:', input.value);
          } else if (isRandom) {
            /** @type {HTMLElement} */ (label).style.display = '';
            input.disabled = false;
            // Set checked without triggering events (silent update)
            if (!input.checked) {
              input.checked = true;
            }
            visibleCount++;
            console.log('[PXO ATC] → Showing and checking random:', input.value);
          }
          break;

        case 'hide-random-and-set':
          // IN-STOCK or PRE-ORDER/BACK-ORDER without random: random 숨김, set 숨김, base 활성화
          if (isRandom || isSet) {
            /** @type {HTMLElement} */ (label).style.display = 'none';
            input.disabled = true;
            console.log('[PXO ATC] → Hiding:', input.value);
          } else if (isBase) {
            /** @type {HTMLElement} */ (label).style.display = '';
            input.disabled = false;
            visibleCount++;
            console.log('[PXO ATC] → Showing base:', input.value);
          }
          break;

        case 'hide-random-and-set-disable-base':
          // OUT-OF-STOCK: random 숨김, set 숨김, base 비활성화 (선긋기)
          if (isRandom || isSet) {
            /** @type {HTMLElement} */ (label).style.display = 'none';
            input.disabled = true;
            console.log('[PXO ATC] → Hiding:', input.value);
          } else if (isBase) {
            /** @type {HTMLElement} */ (label).style.display = '';
            input.disabled = true; // 비활성화
            visibleCount++;
            console.log('[PXO ATC] → Showing but disabling base:', input.value);
          }
          break;

        case 'show-all':
          // Show all options
          /** @type {HTMLElement} */ (label).style.display = '';
          input.disabled = false;
          visibleCount++;
          console.log('[PXO ATC] → Showing:', input.value);
          break;
      }
    });

    // (PXO) Hide entire variant picker if only 1 visible option (single variant products)
    if (visibleCount <= 1) {
      /** @type {HTMLElement} */ (variantPicker).style.display = 'none';
      console.log('[PXO ATC] ✓ Hiding variant picker - only', visibleCount, 'visible option(s)');
    } else {
      /** @type {HTMLElement} */ (variantPicker).style.display = '';
      console.log('[PXO ATC] ✓ Showing variant picker -', visibleCount, 'visible options');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

  const { variantId, addToCartButtonContainer } = this.refs;

  const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
  const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');
  const newAddToCartContainer = event.detail.data.html.querySelector('[ref="addToCartButtonContainer"]');
  const availabilityStatusContainer = /** @type {HTMLElement | null} */ (newAddToCartContainer);
  const availabilityStatus = availabilityStatusContainer?.dataset?.availabilityStatus ?? '';
  const productStatus = availabilityStatusContainer?.dataset?.productStatus ?? '';

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

  let resourceUnavailable = !event.detail.resource || event.detail.resource.available === false;
  if (productStatus === 'preorder' || productStatus === 'backorder') {
    resourceUnavailable = false;
  }
  const disableStatuses = ['temp_out'];
  const shouldDisable = resourceUnavailable || disableStatuses.includes(availabilityStatus);

    if (addToCartButtonContainer) {
      if (availabilityStatus) {
        addToCartButtonContainer.setAttribute('data-availability-status', availabilityStatus);
      } else {
        addToCartButtonContainer.removeAttribute('data-availability-status');
      }
      if (productStatus) {
        addToCartButtonContainer.setAttribute('data-product-status', productStatus);
      } else {
        addToCartButtonContainer.removeAttribute('data-product-status');
      }
    }

    if (productStatus) {
      this.setAttribute('data-product-status', productStatus);
    } else {
      this.removeAttribute('data-product-status');
    }

    // Update the button state
    if (currentAddToCartButton) {
      if (shouldDisable) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
        
        // (PXO) Re-apply button state after morph (button text/color for PRE-ORDER/BACK-ORDER)
        addToCartButtonContainer.updateButtonState();
      }
    }

    // (PXO) Hide accelerated checkout for OUT-OF-STOCK, PRE-ORDER, BACK-ORDER
    // Show only for IN-STOCK
    if (this.refs.acceleratedCheckoutButtonContainer) {
      const productJson = this.#getProductJson();
      const masterStatus = productJson ? getAvailabilityStatus(productJson) : null;
      const hideAcceleratedCheckout = shouldDisable || masterStatus === 'PRE-ORDER' || masterStatus === 'BACK-ORDER';
      
      if (hideAcceleratedCheckout) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        console.log('[PXO] Hiding accelerated checkout - Status:', masterStatus);
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        console.log('[PXO] Showing accelerated checkout - Status:', masterStatus);
      }

      // (PXO) Update info boxes based on status
      if (masterStatus) {
        this.#updateInfoBoxes(masterStatus);
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // --- PXO: Update inventory status (SSOT) ---
    try {
      // (PXO DEBUG) Find container globally using product ID
      const inventoryContainer = document.querySelector(
        `[data-product-id="${this.dataset.productId}"] [data-pxo-inventory-status]`
      );
      console.log('[PXO DEBUG] Variant change - Product ID:', this.dataset.productId);
      console.log('[PXO DEBUG] Variant change - Container:', inventoryContainer);
      
      const productJson = this.#getProductJson();
      const selectedVariant = event.detail.resource;
      
      if (inventoryContainer && productJson && selectedVariant && pxoTranslations) {
        console.log('[PXO DEBUG] Calling updateProductInventory with variant:', selectedVariant.id);
        updateProductInventory(
          /** @type {HTMLElement} */ (inventoryContainer),
          productJson,
          selectedVariant,
          pxoTranslations
        );
      } else {
        console.warn('[PXO DEBUG] Missing data - Container:', !!inventoryContainer, 
          'Product:', !!productJson, 'Variant:', !!selectedVariant, 'Translations:', !!pxoTranslations);
      }
    } catch (e) {
      console.error('[PXO] Error updating inventory status:', e);
    }
    // --- End PXO inventory update ---

    // (PXO) Re-apply variant visibility rules IMMEDIATELY after variant picker HTML is replaced
    // Use requestAnimationFrame to apply rules synchronously after DOM update but before paint
    // Variant picker fetches fresh HTML from server, so we need to re-hide random/set variants for IN-STOCK
    const productJson = this.#getProductJson();
    if (productJson) {
      const status = getAvailabilityStatus(productJson);
      console.log('[PXO] Re-applying variant visibility rules after variant change - Status:', status);
      
      // Apply rules immediately (synchronously) to prevent flash of all variants
      switch (status) {
        case 'PRE-ORDER':
        case 'BACK-ORDER': {
          const randomVariant = this.#findRandomVariant();
          if (randomVariant) {
            this.#applyVariantVisibilityRules('hide-base-and-set');
          } else {
            this.#applyVariantVisibilityRules('hide-random-and-set');
          }
          break;
        }
        case 'IN-STOCK':
          this.#applyVariantVisibilityRules('hide-random-and-set');
          break;
        case 'OUT-OF-STOCK':
          this.#applyVariantVisibilityRules('hide-random-and-set-disable-base');
          break;
        default:
          this.#applyVariantVisibilityRules('show-all');
      }
    }

    // Fetch and update cart quantity for the new variant (after visibility rules applied)
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// ============================================================================
// PXO INVENTORY LOGIC (SSOT - Single Source of Truth)
// ============================================================================

/**
 * Determines master availability status based on business rules
 * @param {Object} product - Full product JSON
 * @returns {'PRE-ORDER' | 'IN-STOCK' | 'BACK-ORDER' | 'OUT-OF-STOCK'}
 */
function getAvailabilityStatus(product) {
  const variants = product?.variants || [];
  const customMetafields = product?.metafields?.custom || {};
  const releaseDateStr = customMetafields.release_date;
  const supplierStock = customMetafields.supplier_stock || false;
  const now = new Date();
  const excludedKeywords = ['random', 'rnd', 'set'];

  console.log('[PXO DEBUG] getAvailabilityStatus - supplier_stock:', supplierStock);
  console.log('[PXO DEBUG] getAvailabilityStatus - release_date:', releaseDateStr);
  console.log('[PXO DEBUG] getAvailabilityStatus - variants count:', variants.length);

  // 1. PRE-ORDER: supplier_stock=true AND release_date > now
  if (supplierStock && releaseDateStr) {
    try {
      const releaseDate = new Date(releaseDateStr);
      console.log('[PXO DEBUG] Release date:', releaseDate, 'vs Now:', now);
      if (releaseDate > now) {
        console.log('[PXO DEBUG] → PRE-ORDER');
        return 'PRE-ORDER';
      }
    } catch (e) {
      console.warn('[PXO] Invalid release_date:', releaseDateStr);
    }
  }

  // 2. Calculate relevant stock (exclude random/set variants)
  const relevantStock = variants
    .filter(v => {
      const lowerTitle = (v.title || '').toLowerCase();
      return !excludedKeywords.some(keyword => lowerTitle.includes(keyword));
    })
    .reduce((sum, v) => sum + (v.inventory_quantity || 0), 0);

  console.log('[PXO DEBUG] Relevant stock:', relevantStock);

  // 3. IN-STOCK: relevant inventory > 0
  if (relevantStock > 0) {
    console.log('[PXO DEBUG] → IN-STOCK');
    return 'IN-STOCK';
  }

  // 4. BACK-ORDER: supplier_stock=true (and not preorder)
  if (supplierStock) {
    console.log('[PXO DEBUG] → BACK-ORDER');
    return 'BACK-ORDER';
  }

  // 5. OUT-OF-STOCK: default fallback
  console.log('[PXO DEBUG] → OUT-OF-STOCK');
  return 'OUT-OF-STOCK';
}

/**
 * Updates inventory status UI (SSOT controller)
 * @param {HTMLElement} container - Element with [data-pxo-inventory-status]
 * @param {Object} product - Full product JSON
 * @param {Object} selectedVariant - Currently selected variant
 * @param {Object} translations - Translated strings
 */
function updateProductInventory(container, product, selectedVariant, translations) {
  if (!container || !product || !selectedVariant || !translations) {
    console.warn('[PXO] Missing required data for inventory update.', { container, product, selectedVariant });
    return;
  }

  const iconEl = container.querySelector('[data-pxo-inventory-icon]');
  const labelEl = container.querySelector('[data-pxo-status-label]');
  const metaEl = container.querySelector('[data-pxo-status-meta]');

  if (!iconEl || !labelEl || !metaEl) {
    console.warn('[PXO] Missing DOM elements in inventory container.');
    return;
  }

  const masterStatus = getAvailabilityStatus(product);
  
  console.log('[PXO DEBUG] updateProductInventory called');
  console.log('[PXO DEBUG] Master Status:', masterStatus);
  console.log('[PXO DEBUG] Product:', product);
  console.log('[PXO DEBUG] Selected Variant:', selectedVariant);

  let finalStatusClass = 'out_of_stock';
  let translationKey = 'availability_status_temp_out';
  let metaText = '';

  switch (masterStatus) {
    case 'PRE-ORDER':
      finalStatusClass = 'preorder';
      translationKey = 'availability_status_preorder';
      const variantDate = selectedVariant.metafields?.custom?.release_date;
      const productDate = product.metafields?.custom?.release_date;
      const dateStr = variantDate || productDate;
      if (dateStr) {
        const formattedDate = formatDate(dateStr);
        metaText = translate('availability_release_date', translations, { date: formattedDate });
      }
      break;

    case 'IN-STOCK':
      finalStatusClass = 'in_stock';
      translationKey = 'availability_status_in_stock';
      break;

    case 'BACK-ORDER':
      finalStatusClass = 'backorder';
      translationKey = 'availability_status_backorder';
      const variantLeadTime = selectedVariant.metafields?.custom?.lead_time_text;
      const productLeadTime = product.metafields?.custom?.lead_time_text;
      const leadTime = variantLeadTime || productLeadTime;
      if (leadTime) metaText = leadTime;
      break;

    case 'OUT-OF-STOCK':
      finalStatusClass = 'out_of_stock';
      translationKey = 'availability_status_temp_out';
      break;
  }

  // Update badge container class for CSS styling
  container.className = `product-availability-badge product-availability-badge--${finalStatusClass}`;
  
  // Update label with translated text
  labelEl.textContent = translate(translationKey, translations);
  console.log('[PXO DEBUG] Label set to:', labelEl.textContent);

  if (metaText) {
    metaEl.textContent = ` · ${metaText}`;
    metaEl.removeAttribute('hidden');
  } else {
    metaEl.textContent = '';
    metaEl.setAttribute('hidden', '');
  }
}

/**
 * Translation helper with variable substitution
 * @param {string} key - Translation key
 * @param {Object} translations - Translations object
 * @param {Object} options - Variables for substitution
 * @returns {string}
 */
function translate(key, translations, options = {}) {
  let translation = translations[key] || key;
  if (typeof translation === 'string') {
    return translation.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, varName) => {
      return options[varName] !== undefined ? options[varName] : match;
    });
  }
  return translation;
}

/**
 * Date formatter (DD.MM.YYYY)
 * @param {string} dateStr - ISO date string
 * @returns {string}
 */
function formatDate(dateStr) {
  try {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return dateStr;
  }
}
